import React, { useState, useImperativeHandle, forwardRef, useMemo, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Slider,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Check } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useGetUsers } from "../../../shared/hooks";
import { AutoComplete } from "../../../shared/components";

export interface PhaseFormHandle {
  submit: () => void;
}

interface PhaseFormProps {
  onSave: (data: PhaseFormData) => void;
  onCancel: () => void;
  initialData?: Partial<PhaseFormData>;
  isEditing?: boolean;
}

export interface PhaseFormData {
  phase: string;
  owner: string;
  userId?: number;
  startDate: Dayjs;
  plannedCompletionDate: Dayjs;
  status: string;
  color: string;
  progress: number;
}

const STATUS_OPTIONS = ["Not Started", "In Progress", "Completed", "On Hold"];

const COLOR_OPTIONS = [
  { value: "#2196F3", label: "Blue" },
  { value: "#FF9800", label: "Orange" },
  { value: "#4CAF50", label: "Green" },
  { value: "#9C27B0", label: "Purple" },
  { value: "#E91E63", label: "Pink" },
];

export const PhaseForm = forwardRef<PhaseFormHandle, PhaseFormProps>(
  function PhaseForm({ onSave, onCancel, initialData, isEditing }, ref) {
    const theme = useTheme();
    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();

    const userOptions = useMemo(() => {
      if (!usersData?.data) return [];
      return usersData.data
        .filter(user => user.is_active === "1")
        .map(user => ({
          id: user.user_id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
        }));
    }, [usersData]);

    const [formData, setFormData] = useState<PhaseFormData>({
      phase: initialData?.phase || "",
      owner: initialData?.owner || "",
      userId: initialData?.userId,
      startDate: initialData?.startDate || dayjs(),
      plannedCompletionDate: initialData?.plannedCompletionDate || dayjs(),
      status: initialData?.status || "Not Started",
      color: initialData?.color || "#2196F3",
      progress: initialData?.progress || 0,
    });

    useEffect(() => {
      if (initialData) {
        setFormData((prev) => ({
          ...prev,
          phase: initialData.phase ?? prev.phase,
          owner: initialData.owner ?? prev.owner,
          userId: initialData.userId ?? prev.userId,
          startDate: initialData.startDate ?? prev.startDate,
          plannedCompletionDate:
            initialData.plannedCompletionDate ?? prev.plannedCompletionDate,
          status: initialData.status ?? prev.status,
          color: initialData.color ?? prev.color,
          progress: initialData.progress ?? prev.progress,
        }));
      }
    }, [initialData]);

    useImperativeHandle(ref, () => ({
      submit: () => {
        onSave(formData);
      },
    }));

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            flex: 1,
            gap: 3,
            p: 2,
          }}
        >

          <AutoComplete
            value={formData.phase}
            onSelect={(value) => {
              setFormData({ ...formData, phase: value })
            }}
            label="Phase"
            name={"phases"}
            isLabelRequired
            disabled={isEditing}
          />

          <FormControl fullWidth>
            <InputLabel>Owner</InputLabel>
            <Select
              value={formData.userId || ""}
              label="Owner"
              onChange={(e) => {
                const selectedUser = userOptions.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  userId: e.target.value as number,
                  owner: selectedUser?.name || ""
                });
              }}
              disabled={isLoadingUsers}
            >
              {isLoadingUsers ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : userOptions.length > 0 ? (
                userOptions.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No users available
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <DateTimePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(date) =>
              date && setFormData({ ...formData, startDate: date })
            }
            slotProps={{ textField: { fullWidth: true } }}
          />

          <DateTimePicker
            label="Planned Completion Date"
            value={formData.plannedCompletionDate}
            onChange={(date) =>
              date && setFormData({ ...formData, plannedCompletionDate: date })
            }
            slotProps={{ textField: { fullWidth: true } }}
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* <Box sx={{ px: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select Color
            </Typography>
            <ToggleButtonGroup
              value={formData.color}
              exclusive
              onChange={(_, value) =>
                value && setFormData({ ...formData, color: value })
              }
              sx={{ gap: 1 }}
            >
              {COLOR_OPTIONS.map((color) => (
                <ToggleButton
                  key={color.value}
                  value={color.value}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: color.value,
                    "&:hover": { bgcolor: color.value, opacity: 0.8 },
                    "&.Mui-selected": {
                      bgcolor: color.value,
                      "&:hover": { bgcolor: color.value, opacity: 0.8 },
                    },
                  }}
                >
                  {formData.color === color.value && (
                    <Check sx={{ color: "white" }} />
                  )}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box> */}

          <Box sx={{ px: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Percentage Completed
            </Typography>
            <Slider
              value={formData.progress}
              onChange={(_, value) =>
                setFormData({ ...formData, progress: value as number })
              }
              valueLabelDisplay="auto"
              step={5}
              marks
              min={0}
              max={100}
              sx={{ mt: 2 }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Typography variant="caption">0</Typography>
              <Typography variant="caption">25</Typography>
              <Typography variant="caption">50</Typography>
              <Typography variant="caption">70</Typography>
              <Typography variant="caption">100%</Typography>
            </Box>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }
);

export const PhaseFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
}> = ({ onSave, onCancel, onDelete, isEditing, isDeleteAllowed }) => (
  <>
    <Button
      variant="outlined"
      onClick={onCancel}
      sx={{ textTransform: "uppercase" }}
    >
      Cancel
    </Button>
    {isEditing && onDelete && isDeleteAllowed && (
      <Button
        variant="outlined"
        color="error"
        onClick={onDelete}
        sx={{ textTransform: "uppercase" }}
      >
        Delete
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onSave}
      sx={{ textTransform: "uppercase", bgcolor: "#E84B0F" }}
    >
      Save Details
    </Button>
  </>
);
